import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { SERVER_BASE_URL } from '../../../environments/environment';
import { MatDialog } from '@angular/material/dialog';
import { CreditOptionDto } from '../../model/credit/credit-option.dto';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { AuthStatusDto } from '../auth/dto/auth-status.dto';
import { EventMessage } from '@app/component/kreditz-iframe/kreditz-iframe.component';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private readonly httpReqOptions = { withCredentials: true };
  webformId = '';
  finapiError = false;

  constructor(private http: HttpClient, private dialogService: MatDialog) {}

  submitForm(form: UserRegistrationForm): Promise<StandardCheckoutResponse> {
    return this.http
      .post<StandardCheckoutResponse>(
        `${SERVER_BASE_URL}/user/de/userRegistrationForm/`,
        form,
        { withCredentials: true }
      )
      .toPromise();
  }

  getActiveUserInfoDe(): Promise<UserRegistrationDto> {
    let params = new HttpParams();

    return this.http
      .get<UserRegistrationDto>(
        `${SERVER_BASE_URL}/user/de/getActiveUserInfo/`,
        {
          ...this.httpReqOptions,
          params: params,
        }
      )
      .toPromise();
  }

  verifyEmail(code: string): Promise<StandardCheckoutResponse> {
    let verificationCode: VerificationCode = {
      code: code,
    };

    return this.http
      .post<StandardCheckoutResponse>(
        `${SERVER_BASE_URL}/user/de/verifyEmail/`,
        verificationCode,
        {
          withCredentials: true,
        }
      )
      .toPromise();
  }

  verifyPhone(code: string): Promise<StandardCheckoutResponse> {
    let verificationCode: VerificationCode = {
      code: code,
    };

    return this.http
      .post<StandardCheckoutResponse>(
        `${SERVER_BASE_URL}/user/de/verifyPhone/`,
        verificationCode,
        {
          withCredentials: true,
        }
      )
      .toPromise();
  }

  verifyUser(
    code: string,
    paymentRequestId: string
  ): Promise<StandardCheckoutResponse> {
    let verificationCode: UserVerificationData = {
      paymentRequestId: paymentRequestId,
      emailVerificationCode: code,
    };

    return this.http
      .post<StandardCheckoutResponse>(
        `${SERVER_BASE_URL}/user/de/verifyUser/`,
        verificationCode,
        {
          withCredentials: true,
        }
      )
      .toPromise();
  }

  registerEmailAndPhone(
    form: EmailAndPhone
  ): Promise<StandardCheckoutResponse> {
    return this.http
      .post<StandardCheckoutResponse>(
        `${SERVER_BASE_URL}/user/de/registerEmailAndPhone/`,
        form,
        { withCredentials: true }
      )
      .toPromise();
  }

  checkExistingUser(form: EmailAndPhone): Promise<StandardCheckoutResponse> {
    return this.http
      .post<StandardCheckoutResponse>(
        `${SERVER_BASE_URL}/user/de/checkExistingUser/`,
        form,
        { withCredentials: true }
      )
      .toPromise();
  }

  registerUser(form: UserRegistrationForm): Promise<StandardCheckoutResponse> {
    return this.http
      .post<StandardCheckoutResponse>(
        `${SERVER_BASE_URL}/user/de/registerUser/`,
        form,
        {
          withCredentials: true,
        }
      )
      .toPromise();
  }

  sendPurchaseConfirmationEmail(): Promise<StandardCheckoutResponse> {
    return this.http
      .post<StandardCheckoutResponse>(
        `${SERVER_BASE_URL}/user/de/sendPurchaseConfirmationEmail/`,
        {
          withCredentials: true,
        }
      )
      .toPromise();
  }

  sendEmailVerificationEmail(): Promise<StandardCheckoutResponse> {
    return this.http
      .post<StandardCheckoutResponse>(
        `${SERVER_BASE_URL}/user/de/sendEmailVerificationEmail/`,
        {
          withCredentials: true,
        }
      )
      .toPromise();
  }

  resendEmailVerificationEmail(): Promise<StandardCheckoutResponse> {
    return this.http
      .post<StandardCheckoutResponse>(
        `${SERVER_BASE_URL}/user/de/resendEmailVerificationEmail/`,
        {
          withCredentials: true,
        }
      )
      .toPromise();
  }

  sendAccountRestorationEmail(): Promise<StandardCheckoutResponse> {
    return this.http
      .post<StandardCheckoutResponse>(
        `${SERVER_BASE_URL}/user/de/sendAccountRestorationEmail/`,
        {
          withCredentials: true,
        }
      )
      .toPromise();
  }

  restoreAccount(code: string): Promise<StandardCheckoutResponse> {
    let verificationCode: VerificationCode = {
      code: code,
    };

    return this.http
      .post<StandardCheckoutResponse>(
        `${SERVER_BASE_URL}/user/de/restoreAccount/`,
        verificationCode,
        {
          withCredentials: true,
        }
      )
      .toPromise();
  }

  updatePhone(code: string): Promise<StandardCheckoutResponse> {
    let verificationCode: UpdatePhoneNumber = {
      phoneNumber: code,
    };

    return this.http
      .post<StandardCheckoutResponse>(
        `${SERVER_BASE_URL}/user/de/updatePhone/`,
        verificationCode,
        {
          withCredentials: true,
        }
      )
      .toPromise();
  }

  resendPhoneVerificationSms(): Promise<StandardCheckoutResponse> {
    return this.http
      .post<StandardCheckoutResponse>(
        `${SERVER_BASE_URL}/user/de/resendPhoneVerificationSms/`,
        {
          withCredentials: true,
        }
      )
      .toPromise();
  }

  confirmTokenizedPayment(): Promise<StandardCheckoutResponse> {
    return this.http
      .post<StandardCheckoutResponse>(
        `${SERVER_BASE_URL}/user/de/confirmTokenizedPayment/`,
        {
          withCredentials: true,
        }
      )
      .toPromise();
  }

  resendAccountRestorationEmail(): Promise<StandardCheckoutResponse> {
    return this.http
      .post<StandardCheckoutResponse>(
        `${SERVER_BASE_URL}/user/de/resendAccountRestorationEmail/`,
        {
          withCredentials: true,
        }
      )
      .toPromise();
  }

  resendPurchaseConfirmationEmail(): Promise<StandardCheckoutResponse> {
    return this.http
      .post<StandardCheckoutResponse>(
        `${SERVER_BASE_URL}/user/de/resendPurchaseConfirmationEmail/`,
        {
          withCredentials: true,
        }
      )
      .toPromise();
  }

  getFinapiNewConnectionWebForm(): Observable<any> {
    return this.http
      .get(`${SERVER_BASE_URL}/user/de/getFinapiNewConnectionWebform`, {
        ...this.httpReqOptions,
        responseType: 'text',
      })
      .pipe(
        tap((webformId: any) => {
          this.webformId = webformId;
        }),
        catchError((error: any) => {
          this.finapiError = true;
          console.log('Error when loading new finApi webform: ' + error);
          return throwError(error);
        })
      );
  }

  getFinapiUpdateConnectionWebform(): Observable<UpdateConnectionWebFormReponse> {
    return this.http
      .get<UpdateConnectionWebFormReponse>(
        `${SERVER_BASE_URL}/user/de/getFinapiUpdateConnectionWebform`,
        {
          ...this.httpReqOptions,
          responseType: 'json',
        }
      )
      .pipe(
        tap((resp) => {
          if (resp.webformId) {
            this.webformId = resp.webformId;
          }
        }),
        catchError((error: any) => {
          this.finapiError = true;
          console.log('Error when loading update finApi webform: ' + error);
          return throwError(error);
        })
      );
  }

  pollForFinapiUpdateConnectionWebform(
    taskId: string
  ): Observable<UpdateConnectionWebFormReponse> {
    let params = new HttpParams();
    if (taskId) {
      params = params.append('taskId', taskId);
    }

    return this.http.get<UpdateConnectionWebFormReponse>(
      `${SERVER_BASE_URL}/user/de/pollForFinapiUpdateConnectionWebform`,
      {
        ...this.httpReqOptions,
        params: params,
      }
    );
  }

  fetchFinApiReport(webformId: string): Promise<StandardCheckoutResponse> {
    let params = new HttpParams();
    if (webformId) {
      params = params.append('webformId', webformId);
    }

    return this.http
      .get<StandardCheckoutResponse>(
        `${SERVER_BASE_URL}/user/de/fetchFinapiReport/`,
        {
          ...this.httpReqOptions,
          params: params,
        }
      )
      .toPromise();
  }

  pollForFinapiReport(webformId: string): Observable<boolean> {
    let params = new HttpParams();
    if (webformId) {
      params = params.append('webformId', webformId);
    }

    return this.http.get<boolean>(
      `${SERVER_BASE_URL}/user/de/pollForFinapiReport/`,
      {
        ...this.httpReqOptions,
        params: params,
      }
    );
  }

  pollForFinapiPaymentWebform(webformId: string): Observable<boolean> {
    let params = new HttpParams();
    if (webformId) {
      params = params.append('webformId', webformId);
    }

    return this.http.get<boolean>(
      `${SERVER_BASE_URL}/user/de/pollForPaymentWebform/`,
      {
        ...this.httpReqOptions,
        params: params,
      }
    );
  }

  startFinapiStandalonePayment(): Observable<FinapiPaymentPollResult> {
    return this.http.get<FinapiPaymentPollResult>(
      `${SERVER_BASE_URL}/user/de/startFinapiStandalonePayment/`,
      {
        ...this.httpReqOptions,
      }
    );
  }

  fetchFtsReport(transactionId: string): Promise<StandardCheckoutResponse> {
    let params = new HttpParams();
    if (transactionId) {
      params = params.append('transactionId', transactionId);
    }

    return this.http
      .get<StandardCheckoutResponse>(
        `${SERVER_BASE_URL}/user/de/fetchFtsResult/`,
        {
          ...this.httpReqOptions,
          params: params,
        }
      )
      .toPromise();
  }

  getX2saSession(): Promise<Xs2aSessionDto> {
    return this.http
      .get<Xs2aSessionDto>(`${SERVER_BASE_URL}/user/de/getFtsSessionKey`)
      .toPromise();
  }

  logFtsError(errorCode: string, messages: string, recoverable: string) {
    let params = new HttpParams();
    if (errorCode) {
      params = params.append('errorCode', errorCode);
    }

    if (messages) {
      params = params.append('messages', messages);
    }

    if (recoverable) {
      params = params.append('recoverable', recoverable);
    }

    return this.http.get(`${SERVER_BASE_URL}/user/de/logFtsError/`, {
      ...this.httpReqOptions,
      params: params,
    });
  }

  fetchRegisteredPhone(): Promise<PhoneNumber> {
    return this.http
      .get<PhoneNumber>(`${SERVER_BASE_URL}/user/public/phone`, {
        withCredentials: true,
      })
      .toPromise();
  }

  registerPhone(phone: string): Promise<PhoneNumber> {
    return this.http
      .post<PhoneNumber>(
        `${SERVER_BASE_URL}/request/settle/setPhone/${phone}`,
        {}
      )
      .toPromise();
  }

  fetchRegisteredEmail(): Promise<EmailDto> {
    return this.http
      .get<EmailDto>(`${SERVER_BASE_URL}/user/email`, {
        withCredentials: true,
      })
      .toPromise();
  }

  fetchPreExistingContactInfo(): Promise<UserRegistrationDto> {
    return this.http
      .get<UserRegistrationDto>(
        `${SERVER_BASE_URL}/user/public/getPreExistingContactInfo`,
        {
          withCredentials: true,
        }
      )
      .toPromise();
  }

  registerEmail(email: string): Promise<EmailDto> {
    const emailDto: EmailDto = {
      email: email,
    };

    return this.http
      .post<EmailDto>(`${SERVER_BASE_URL}/user/email/register`, emailDto, {
        withCredentials: true,
      })
      .toPromise();
  }

  selectInstallmentPlan(loanOfferId: string): Promise<String> {
    const loanOffer = {
      id: loanOfferId,
    };

    return this.http
      .post<string>(
        `${SERVER_BASE_URL}/user/de/selectInstallmentPlan/`,
        loanOffer,
        {
          withCredentials: true,
        }
      )
      .toPromise();
  }

  cancelPayment(): Promise<StandardCheckoutResponse> {
    return this.http
      .post<StandardCheckoutResponse>(
        `${SERVER_BASE_URL}/user/de/cancelPayment/`,
        {
          withCredentials: true,
        }
      )
      .toPromise();
  }

  cancelPayerToken(): Promise<StandardCheckoutResponse> {
    return this.http
      .post<StandardCheckoutResponse>(
        `${SERVER_BASE_URL}/user/de/cancelPayerToken/`,
        {
          withCredentials: true,
        }
      )
      .toPromise();
  }

  registerIban(
    iban: string,
    directDebitConsentData: string
  ): Promise<StandardCheckoutResponse> {
    return this.http
      .post<StandardCheckoutResponse>(
        `${SERVER_BASE_URL}/user/de/registerIban`,
        {
          iban: iban,
          directDebitConsentData: {
            agreementText: directDebitConsentData,
          },
        },
        {
          withCredentials: true,
        }
      )
      .toPromise();
  }

  registerManualIbanInput(
    iban: string,
    directDebitConsentData: string
  ): Observable<StandardCheckoutResponse> {
    return this.http.post<StandardCheckoutResponse>(
      `${SERVER_BASE_URL}/user/de/registerManualIbanInput`,
      {
        iban: iban,
        directDebitConsentData: {
          agreementText: directDebitConsentData,
        },
      },
      {
        withCredentials: true,
      }
    );
  }

  chargeCollectiveInvoice(): Promise<StandardCheckoutResponse> {
    return this.http
      .post<StandardCheckoutResponse>(
        `${SERVER_BASE_URL}/user/de/confirmCollectiveInvoice`,
        {}
      )
      .toPromise();
  }

  fetchIbans(): Promise<StandardCheckoutResponse> {
    return this.http
      .get<StandardCheckoutResponse>(`${SERVER_BASE_URL}/user/de/getIbans`, {
        withCredentials: true,
      })
      .toPromise();
  }

  //bank id auth
  initAuthenticate(): Observable<AuthStatusDto> {
    return this.http.get<AuthStatusDto>(
      `${SERVER_BASE_URL}/user/se/authenticate/init`,
      {
        withCredentials: true,
      }
    );
  }

  completeAuthenticate(): Observable<AuthStatusDto> {
    return this.http.get<AuthStatusDto>(
      `${SERVER_BASE_URL}/user/se/authenticate/complete`,
      {
        withCredentials: true,
      }
    );
  }

  checkOngoingAuthentication(): Promise<Boolean> {
    return this.http.get<Boolean>(`${SERVER_BASE_URL}/user/se/checkIsOngoingAuthentication`,
      this.httpReqOptions
    ).toPromise();
  }

  initSign(): Observable<AuthStatusDto> {
    return this.http.get<AuthStatusDto>(
      `${SERVER_BASE_URL}/user/se/sign/init`,
      {
        withCredentials: true,
      }
    );
  }

  completeSign(): Observable<AuthStatusDto> {
    return this.http.get<AuthStatusDto>(
      `${SERVER_BASE_URL}/user/se/sign/complete`,
      {
        withCredentials: true,
      }
    );
  }

  selectAccount(
    selectAccountData: SelectAccountData
  ): Observable<SelectAccountResp> {
    return this.http.post<SelectAccountResp>(
      `${SERVER_BASE_URL}/user/se/directDebit/account/selectNew`,
      { ...selectAccountData },
      {
        withCredentials: true,
      }
    );
  }

  getAccount(): Observable<AccountInformation> {
    return this.http.get<AccountInformation>(
      `${SERVER_BASE_URL}/user/se/directDebit/account/selected/`,
      {
        withCredentials: true,
      }
    );
  }

  getContactDetailsSe(): Promise<EmailAndPhone> {
    return this.http
      .get<EmailAndPhone>(
        `${SERVER_BASE_URL}/user/se/contactDetails`,
        this.httpReqOptions
      )
      .toPromise();
  }

  registerEmailAndPhoneSE(
    form: EmailAndPhone
  ): Promise<StandardCheckoutResponse> {
    return this.http
      .post<StandardCheckoutResponse>(
        `${SERVER_BASE_URL}/user/se/registerEmailAndPhone/`,
        form,
        { withCredentials: true }
      )
      .toPromise();
  }

  confirmInstantDebit(
    accountId: string,
    directDebitTermsLink: string
  ): Promise<StandardCheckoutResponse> {
    const params = new HttpParams()
      .set('accountId', accountId)
      .set('directDebitTermsLink', directDebitTermsLink);

    return this.http
      .post<StandardCheckoutResponse>(
        `${SERVER_BASE_URL}/instantdebit/confirmInstantDebit`,
        {},
        {
          params: params,
          ...this.httpReqOptions,
        }
      )
      .toPromise();
  }

  // Confirming a flow with bank account selection - payments AND onboardings
  confirmFlow(): Promise<StandardCheckoutResponse> {
    return this.http
      .post<StandardCheckoutResponse>(
        `${SERVER_BASE_URL}/user/se/confirmFlow/`,
        {},
        { withCredentials: true }
      )
      .toPromise();
  }

  confirmTokenizedOnboardingInHouse(
    accountId: string,
    directDebitTermsLink: string
  ): Promise<StandardCheckoutResponse> {
    const params = new HttpParams()
      .set('accountId', accountId)
      .set('directDebitTermsLink', directDebitTermsLink);

    return this.http
      .post<StandardCheckoutResponse>(
        `${SERVER_BASE_URL}/user/se/confirm-in-house-tokenized-onboarding`,
        {},
        {
          params: params,
          ...this.httpReqOptions,
        }
      )
      .toPromise();
  }

  confirmTokenizedOnboardingInvoice(): Promise<StandardCheckoutResponse> {
    return this.http
      .post<StandardCheckoutResponse>(
        `${SERVER_BASE_URL}/user/se/confirm-tokenized-onboarding/`,
        {},
        { withCredentials: true }
      )
      .toPromise();
  }

  verifyIban(iban: string): Observable<VerifiedBankDataResp> {
    const params = new HttpParams().set('iban', iban);

    return this.http.get<VerifiedBankDataResp>(
      `${SERVER_BASE_URL}/user/de/verify/iban/`,
      {
        ...this.httpReqOptions,
        params: params,
      }
    );
  }

  setupAISForIbanRegistration(): Promise<StandardCheckoutResponse> {
    return this.http
      .post<StandardCheckoutResponse>(
        `${SERVER_BASE_URL}/user/de/setup/AIS/IbanRegistration`,
        {},
        { withCredentials: true }
      )
      .toPromise();
  }

  getKreditzAccountCheckUrl(
    redirectRoute: string,
    locale: string
  ): Observable<KreditzUrlResponse> {
    let params = new HttpParams();

    params = params.append('redirectRoute', redirectRoute);

    if (locale) {
      params = params.append('locale', locale);
    }

    return this.http.get<KreditzUrlResponse>(
      `${SERVER_BASE_URL}/ais/se/kreditz/account-check`,
      {
        withCredentials: true,
        params: params,
      }
    );
  }

  updateKreditzAccountCheckStatus(
    caseId: string,
    status: EventMessage
  ): Observable<KreditzFormStatusResponse> {
    let eventStatus: string;
    switch (status) {
      case EventMessage.COMPLETED:
        eventStatus = 'COMPLETED';
        break;
      case EventMessage.ERROR:
        eventStatus = 'ERROR';
        break;
      case EventMessage.USER_CANCELLED:
        eventStatus = 'USER_CANCELLED';
        break;
    }

    const body: KreditzAccountCheckStatusRequest = {
      caseId: caseId,
      status: eventStatus,
    };

    return this.http.post<KreditzFormStatusResponse>(
      `${SERVER_BASE_URL}/ais/se/kreditz/account-check/status`,
      body,
      {
        withCredentials: true,
      }
    );
  }
}

export interface VerifiedBankDataResp {
  iban: string;
  verified: boolean;
  bankName: string;
  bankIconUrl: string;
}

export interface SelectAccountResp {
  selectAccountUrl: string;
}

export interface SelectAccountData {
  successEndpoint: string;
  failEndpoint: string;
}

export interface UpdatePhoneNumber {
  phoneNumber: string;
}
export class InstallmentOptionsDto {
  preferredInstallmentPlanDurations: number[];
  installmentOptions: CreditOptionDto[];
}

export class UserRegistrationForm {
  firstName: string;
  familyName: string;
  streetName: string;
  houseNumber: string;
  zipCode: string;
  cityName: string;
  countryCode: string;
  birthDate: string;
  email: string;
}

export interface UserFormData {
  value: string;
  editable: boolean;
}

export class UserRegistrationDto {
  firstName: UserFormData;
  familyName: UserFormData;
  streetName: UserFormData;
  houseNumber: UserFormData;
  zipCode: UserFormData;
  cityName: UserFormData;
  countryCode: UserFormData;
  birthDate: string;
  mayEditBirthDate: boolean;
  email: UserFormData;
  phoneNumber: UserFormData;
}

export class StandardCheckoutResponse {
  success: boolean;
  error: boolean;
  rejected: boolean;
}

export interface UpdateConnectionWebFormReponse {
  webformId?: string;
  taskId?: string;
}

export interface FinapiPaymentPollResult {
  webformId?: string;
  success: boolean;
}

export interface UserRegistrationVM {
  firstName: UserFormDataVM;
  familyName: UserFormDataVM;
  streetName: UserFormDataVM;
  houseNumber: UserFormDataVM;
  zipCode: UserFormDataVM;
  cityName: UserFormDataVM;
  countryCode: UserFormDataVM;
  birthDate: UserFormDataVM;
  email: UserFormDataVM;
  phoneNumber: UserFormDataVM;
}
export interface UserFormDataVM {
  value: string;
  editable: boolean;
}

export class FinApiResponse {
  webformId: string;
}

export class UserInfo {
  userExists: boolean;
  formInfo: UserRegistrationForm;
}

export class Xs2aSessionDto {
  sessionKey: string;
  transactionId: string;
}

export class EmailAndPhone {
  email: string;
  phoneNumber: string;
}

export class VerificationCode {
  code: string;
}

export class UserVerificationData {
  paymentRequestId: string;
  emailVerificationCode: string;
}

export interface EmailDto {
  email: string;
}

export interface AccountInformation {
  mandateStatus: string;
  bank: string;
  description: string;
  lastDigits: string;
  bankLogoUrl: string;
  cannotChangeAccountReason: string;
}

export interface CheckoutResponseUrl extends StandardCheckoutResponse {
  url?: string;
}

export interface PhoneNumber {
  phone: string;
}

export interface KreditzUrlResponse {
  kreditzFormId: string;
  url: string;
}

export interface KreditzFormStatusResponse {
  kreditzFormId?: string;
  success: boolean;
  error: boolean;
}

export interface KreditzAccountCheckStatusRequest {
  caseId: string;
  status: string;
}
